import _ from "lodash";
import {
  call,
  put,
  takeLatest
} from "redux-saga/effects";
import {
  uploadFile,
  downloadXLSFile,
  downloadFile,
  authorizedRequest
} from "utils/api";
import {
  multipleObjectReturn
} from "shared-utility/store/utils/helpers";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";

import {
  defaultReturn,
  makePayload,
  generateDefaultActions,
  subKeySaga,
  callbackSaga,
  requestSaga,
  executeCallback,
  fillDictionary
} from "../utils/helpers";

const initialState = {
  error: "",
  dealCashflows: {},
  uploadErrorMessage: "",
  showSpinner: false,
  uploadResult: {
    wasSuccessful: false
  },
  isDVActive: false
};

export const CLEAR_UPLOAD_ERRORS = generateDefaultActions("CLEAR_UPLOAD_ERRORS");
export const clearUploadErrors = payload => makePayload(CLEAR_UPLOAD_ERRORS.value, payload);
function* clearUploadErrorsSaga(payload) {
  yield put(CLEAR_UPLOAD_ERRORS.createSuccess({ userFriendlyError: "" }));
}

export const GET_DEAL_CASHFLOWS = generateDefaultActions("GET_DEAL_CASHFLOWS");
export const getDealCashflows = payload => makePayload(GET_DEAL_CASHFLOWS.value, payload);
function* getDealCashflowsSaga(payload) {
  const url = `/getDealCashflows/${payload.dealId}`;
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_DEAL_CASHFLOWS, url, "GET");
}

export const GET_QUOTE_CASHFLOWS = generateDefaultActions("GET_QUOTE_CASHFLOWS");
export const getQuoteCashflows = payload => makePayload(GET_QUOTE_CASHFLOWS.value, payload);
function* getQuoteCashflowsSaga(payload) {
  const url = "/getQuoteCashflows";
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_QUOTE_CASHFLOWS, url, "POST", payload, url);
}

export const GET_COMBINED_QUOTE_CASHFLOWS = generateDefaultActions("GET_COMBINED_QUOTE_CASHFLOWS");
export const getCombinedQuoteCashflows = payload => makePayload(GET_COMBINED_QUOTE_CASHFLOWS.value, payload);
function* getCombinedQuoteCashflowsSaga(payload) {
  const url = "/getCombinedQuoteCashflows";
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_COMBINED_QUOTE_CASHFLOWS, url, "POST", payload, url);
}

export const DOWNLOAD_LOAN_CASHFLOWS = generateDefaultActions("DOWNLOAD_LOAN_CASHFLOWS");
export const downloadLoanCashflows = payload => makePayload(DOWNLOAD_LOAN_CASHFLOWS.value, payload);
function* downloadLoanCashflowsSaga(payload) {
  const url = `/deals/${payload.dealId}/loans/${payload.loanId}/exportCashflows`;
  yield call(downloadXLSFile, url, "POST", payload, "");
}

export const DOWNLOAD_HEDGE_CASHFLOWS = generateDefaultActions("DOWNLOAD_HEDGE_CASHFLOWS");
export const downloadHedgeCashflows = payload => makePayload(DOWNLOAD_HEDGE_CASHFLOWS.value, payload);
function* downloadHedgeCashflowsSaga(payload) {
  const url = `/deals/${payload.dealId}/hedges/${payload.hedgeId}/exportCashflows?cashflowType=${payload.cashflowType}`;
  yield call(downloadXLSFile, url, "POST", payload, "");
}

export const DOWNLOAD_COMBINED_CASHFLOWS = generateDefaultActions("DOWNLOAD_COMBINED_CASHFLOWS");
export const downloadCombinedCashflows = payload => makePayload(DOWNLOAD_COMBINED_CASHFLOWS.value, payload);
function* downloadCombinedCashflowsSaga(payload) {
  const url = `/deals/${payload.dealId}/exportCombinedCashflows`;
  yield call(downloadXLSFile, url, "POST", payload, "");
}

export const RESET_LOAN_CASHFLOW_OVERRIDES = generateDefaultActions("RESET_LOAN_CASHFLOW_OVERRIDES");
export const resetLoanCashflowOverrides = payload => makePayload(RESET_LOAN_CASHFLOW_OVERRIDES.value, payload);
function* resetLoanCashflowOverridesSaga(payload) {
  const url = `/loans/${payload.loanId}/resetCashflowOverrides`;
  yield* requestSaga(RESET_LOAN_CASHFLOW_OVERRIDES, url, "POST");
  yield* calculateLoanCashflowsSaga({ loanId: payload.loanId });
  yield* getDealCashflowsSaga({ dealId: payload.dealId, subKey: payload.dealId });
}

export const RESET_HEDGE_CASHFLOW_OVERRIDES = generateDefaultActions("RESET_HEDGE_CASHFLOW_OVERRIDES");
export const resetHedgeCashflowOverrides = payload => makePayload(RESET_HEDGE_CASHFLOW_OVERRIDES.value, payload);
function* resetHedgeCashflowOverridesSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/resetCashflowOverrides`;
  yield* requestSaga(RESET_HEDGE_CASHFLOW_OVERRIDES, url, "POST");
  yield* calculateHedgeCashflowsSaga({ hedgeId: payload.hedgeId });
  yield* getDealCashflowsSaga({ dealId: payload.dealId, subKey: payload.dealId });
}

export const DOWNLOAD_PRINCIPAL_TEMPLATE = generateDefaultActions("DOWNLOAD_PRINCIPAL_TEMPLATE");
export const downloadPrincipalTemplate = payload => makePayload(DOWNLOAD_PRINCIPAL_TEMPLATE.value, payload);
function* downloadPrincipalTemplateSaga(payload) {
  const url = `/loans/${payload.loanId}/downloadPrincipalTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_PRINCIPAL_TEMPLATE.createFailure(error));
  }
}

export const DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE = generateDefaultActions("DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE");
export const downloadPrincipalAndInterestPaymentTemplate = payload => makePayload(DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE.value, payload);
function* downloadPrincipalAndInterestPaymentTemplateSaga(payload) {
  const url = `/loans/${payload.loanId}/downloadPrincipalAndInterestPaymentTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE.createFailure(error));
  }
}

export const UPLOAD_LOAN_CASHFLOWS_OVERRIDE = generateDefaultActions("UPLOAD_LOAN_CASHFLOWS_OVERRIDE");
export const uploadLoanCashflowsOverride = payload => makePayload(UPLOAD_LOAN_CASHFLOWS_OVERRIDE.value, payload);
function* uploadLoanCashflowsOverrideSaga(payload) {
  const url = `/loans/${payload.loanId}/uploadCashflowsOverrideTemplate`;
  payload.url = url;
  const uploadResult = {
    wasSuccessful: false
  };
  try {
    const response = yield call(uploadFile, payload);
    uploadResult.wasSuccessful = true;
    yield put(UPLOAD_LOAN_CASHFLOWS_OVERRIDE.createSuccess({ userFriendlyError: "", uploadResult }));
    yield* calculateLoanCashflowsSaga({ loanId: payload.loanId });
    yield* getDealCashflowsSaga({ dealId: payload.dealId, subKey: payload.dealId });
  } catch (error) {
    const userFriendlyError = _.get(error, "response.data.error", "Upload encountered error. Please choose a different file and try again.");
    yield put(UPLOAD_LOAN_CASHFLOWS_OVERRIDE.createFailure({ userFriendlyError, uploadResult }));
  }
}

export const DOWNLOAD_CAP_TEMPLATE = generateDefaultActions("DOWNLOAD_CAP_TEMPLATE");
export const downloadCapTemplate = payload => makePayload(DOWNLOAD_CAP_TEMPLATE.value, payload);
function* downloadCapTemplateSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/downloadCapTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_CAP_TEMPLATE.createFailure(error));
  }
}

export const DOWNLOAD_SWAP_TEMPLATE = generateDefaultActions("DOWNLOAD_SWAP_TEMPLATE");
export const downloadSwapTemplate = payload => makePayload(DOWNLOAD_SWAP_TEMPLATE.value, payload);
function* downloadSwapTemplateSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/downloadSwapTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_SWAP_TEMPLATE.createFailure(error));
  }
}

export const DOWNLOAD_COLLAR_TEMPLATE = generateDefaultActions("DOWNLOAD_COLLAR_TEMPLATE");
export const downloadCollarTemplate = payload => makePayload(DOWNLOAD_COLLAR_TEMPLATE.value, payload);
function* downloadCollarTemplateSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/downloadCollarTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_COLLAR_TEMPLATE.createFailure(error));
  }
}

export const DOWNLOAD_CORRIDOR_TEMPLATE = generateDefaultActions("DOWNLOAD_CORRIDOR_TEMPLATE");
export const downloadCorridorTemplate = payload => makePayload(DOWNLOAD_CORRIDOR_TEMPLATE.value, payload);
function* downloadCorridorTemplateSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/downloadCorridorTemplate`;
  try {
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_CORRIDOR_TEMPLATE.createFailure(error));
  }
}

export const UPLOAD_HEDGE_CASHFLOWS_OVERRIDE = generateDefaultActions("UPLOAD_HEDGE_CASHFLOWS_OVERRIDE");
export const uploadHedgeCashflowsOverride = payload => makePayload(UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.value, payload);
function* uploadHedgeCashflowsOverrideSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/uploadCashflowsOverrideTemplate`;
  payload.url = url;
  const uploadResult = {
    wasSuccessful: false
  };
  try {
    const response = yield call(uploadFile, payload);
    uploadResult.wasSuccessful = true;
    yield put(UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.createSuccess({ userFriendlyError: "", uploadResult }));
    yield* calculateHedgeCashflowsSaga({ hedgeId: payload.hedgeId });
    yield* getDealCashflowsSaga({ dealId: payload.dealId, subKey: payload.dealId });
  } catch (error) {
    const userFriendlyError = _.get(error, "response.data.error", "Upload encountered error. Please choose a different file and try again.");
    yield put(UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.createFailure({ userFriendlyError, uploadResult }));
  }
}

export const CALCULATE_LOAN_CASHFLOWS = generateDefaultActions("CALCULATE_LOAN_CASHFLOWS");
export const calculateLoanCashflows = payload => makePayload(CALCULATE_LOAN_CASHFLOWS.value, payload);
function* calculateLoanCashflowsSaga(payload) {
  const url = `/loans/${payload.loanId}/calculateLoanCashflows`;
  yield requestSaga(CALCULATE_LOAN_CASHFLOWS, url, "POST");
}

export const CALCULATE_HEDGE_CASHFLOWS = generateDefaultActions("CALCULATE_HEDGE_CASHFLOWS");
export const calculateHedgeCashflows = payload => makePayload(CALCULATE_HEDGE_CASHFLOWS.value, payload);
function* calculateHedgeCashflowsSaga(payload) {
  const url = `/hedges/${payload.hedgeId}/calculateHedgeCashflows`;
  yield requestSaga(CALCULATE_HEDGE_CASHFLOWS, url, "POST");
}

export const CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION = generateDefaultActions("CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION");
export const getDebtValuationActivationStatus = payload => makePayload(CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION.value, payload);
function* getDebtValuationActivationStatusSaga(payload) {
  const url = "/apps/3fa9c4d0-c4c1-4428-ac86-66129387388e/isActivated";
  yield requestSaga(CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION, url);
}

export default function cashflowsReducer(state = initialState, action) {
  const {
    type,
    subKey,
    payload
  } = action;
  switch (type) {
    case GET_QUOTE_CASHFLOWS.value:
      return defaultReturn(state, null, "dealCashflows");
    case GET_QUOTE_CASHFLOWS.success:
    case GET_COMBINED_QUOTE_CASHFLOWS.success:
      return fillDictionary(state, payload.data.cashflows, "dealCashflows", subKey);
    case GET_DEAL_CASHFLOWS.success:
      return multipleObjectReturn(state, ["dealCashflows", "guestPermission"], [payload.data, payload.guestPermission], [subKey, null]);
    case GET_DEAL_CASHFLOWS.failure:
      ToastError("Unable to retrieve cashflows.");

      return state;
    case CLEAR_UPLOAD_ERRORS.success:
      return { ...state, uploadErrorMessage: payload.userFriendlyError };
    case DOWNLOAD_LOAN_CASHFLOWS.success:
      return state;
    case DOWNLOAD_HEDGE_CASHFLOWS.success:
      return state;
    case DOWNLOAD_COMBINED_CASHFLOWS.success:
      return state;
    case DOWNLOAD_PRINCIPAL_TEMPLATE.success:
      return state;
    case DOWNLOAD_PRINCIPAL_TEMPLATE.failure:
      ToastError("Unable to download principal template");

      return state;
    case DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE.success:
      return state;
    case DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE.failure:
      ToastError("Unable to download principal and interest payment template");

      return state;
    case DOWNLOAD_CAP_TEMPLATE.success:
      return state;
    case DOWNLOAD_CAP_TEMPLATE.failure:
      ToastError("Unable to download cap template");

      return state;
    case DOWNLOAD_SWAP_TEMPLATE.success:
      return state;
    case DOWNLOAD_SWAP_TEMPLATE.failure:
      ToastError("Unable to download swap template");

      return state;
    case DOWNLOAD_COLLAR_TEMPLATE.success:
      return state;
    case DOWNLOAD_COLLAR_TEMPLATE.failure:
      ToastError("Unable to download collar template");

      return state;
    case DOWNLOAD_CORRIDOR_TEMPLATE.success:
      return state;
    case DOWNLOAD_CORRIDOR_TEMPLATE.failure:
      ToastError("Unable to download corridor template");

      return state;
    case UPLOAD_LOAN_CASHFLOWS_OVERRIDE.value:
      return { ...state, showSpinner: true };
    case UPLOAD_LOAN_CASHFLOWS_OVERRIDE.success:
      ToastSuccess("Uploaded file successfully");

      return { ...state, uploadErrorMessage: payload.userFriendlyError, uploadResult: payload.uploadResult, showSpinner: false };
    case UPLOAD_LOAN_CASHFLOWS_OVERRIDE.failure:
      return { ...state, uploadErrorMessage: payload.userFriendlyError, uploadResult: payload.uploadResult, showSpinner: false };
    case UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.success:
      ToastSuccess("Uploaded file successfully");

      return { ...state, uploadErrorMessage: payload.userFriendlyError, uploadResult: payload.uploadResult, showSpinner: false };
    case UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.failure:
      return { ...state, uploadErrorMessage: payload.userFriendlyError };
    case DOWNLOAD_LOAN_CASHFLOWS.failure:
      ToastError("Unable to download cashflows");

      return state;
    case DOWNLOAD_HEDGE_CASHFLOWS.failure:
      ToastError("Unable to download cashflows");

      return state;
    case DOWNLOAD_COMBINED_CASHFLOWS.failure:
      ToastError("Unable to download combined cashflows");

      return state;
    case CALCULATE_LOAN_CASHFLOWS.failure:
      ToastError("Unable to recalculate cashflows");

      return state;
    case CALCULATE_HEDGE_CASHFLOWS.failure:
      ToastError("Unable to recalculate cashflows");

      return state;
    case RESET_LOAN_CASHFLOW_OVERRIDES.success:
      ToastSuccess("Successfully reset cashflows");

      return state;
    case RESET_HEDGE_CASHFLOW_OVERRIDES.success:
      ToastSuccess("Successfully reset cashflows");

      return state;
    case CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION.success:
      return defaultReturn(state, payload.data, "isDVActive");
    default:
      return state;
  }
}

export function* cashflowsSaga() {
  yield takeLatest(GET_DEAL_CASHFLOWS.value, getDealCashflowsSaga);
  yield takeLatest(DOWNLOAD_LOAN_CASHFLOWS.value, downloadLoanCashflowsSaga);
  yield takeLatest(DOWNLOAD_HEDGE_CASHFLOWS.value, downloadHedgeCashflowsSaga);
  yield takeLatest(DOWNLOAD_COMBINED_CASHFLOWS.value, downloadCombinedCashflowsSaga);
  yield takeLatest(RESET_LOAN_CASHFLOW_OVERRIDES.value, resetLoanCashflowOverridesSaga);
  yield takeLatest(RESET_HEDGE_CASHFLOW_OVERRIDES.value, resetHedgeCashflowOverridesSaga);
  yield takeLatest(DOWNLOAD_PRINCIPAL_TEMPLATE.value, downloadPrincipalTemplateSaga);
  yield takeLatest(DOWNLOAD_PRINCIPAL_AND_INTEREST_PAYMENT_TEMPLATE.value, downloadPrincipalAndInterestPaymentTemplateSaga);
  yield takeLatest(UPLOAD_LOAN_CASHFLOWS_OVERRIDE.value, uploadLoanCashflowsOverrideSaga);
  yield takeLatest(DOWNLOAD_CAP_TEMPLATE.value, downloadCapTemplateSaga);
  yield takeLatest(DOWNLOAD_SWAP_TEMPLATE.value, downloadSwapTemplateSaga);
  yield takeLatest(DOWNLOAD_COLLAR_TEMPLATE.value, downloadCollarTemplateSaga);
  yield takeLatest(DOWNLOAD_CORRIDOR_TEMPLATE.value, downloadCorridorTemplateSaga);
  yield takeLatest(UPLOAD_HEDGE_CASHFLOWS_OVERRIDE.value, uploadHedgeCashflowsOverrideSaga);
  yield takeLatest(CLEAR_UPLOAD_ERRORS.value, clearUploadErrorsSaga);
  yield takeLatest(CALCULATE_LOAN_CASHFLOWS.value, calculateLoanCashflowsSaga);
  yield takeLatest(CALCULATE_HEDGE_CASHFLOWS.value, calculateHedgeCashflowsSaga);
  yield takeLatest(GET_QUOTE_CASHFLOWS.value, getQuoteCashflowsSaga);
  yield takeLatest(GET_COMBINED_QUOTE_CASHFLOWS.value, getCombinedQuoteCashflowsSaga);
  yield takeLatest(CASHFLOWS_GET_DEBT_VALUATION_ACTIVATION.value, getDebtValuationActivationStatusSaga);
}
